import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * The restock signup button and dialog. Posts the form to the preorder app
 * through the shop's app proxy and shows its answer. The app answers
 * `{ ok: true }` or `{ ok: false, reason }`; it never returns customer text, so
 * the texts come from the locale strings on the element's data attributes.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLDialogElement} dialog
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} email
 * @property {HTMLElement} error
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} status
 *
 * @extends DialogComponent
 */
class RestockSignupComponent extends DialogComponent {
  requiredRefs = ['trigger', 'dialog', 'form', 'email', 'error', 'submit', 'status'];

  #sending = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#returnFocus);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogCloseEvent.eventName, this.#returnFocus);
  }

  /** Safari does not focus a clicked button, so the dialog cannot restore focus to it on its own. */
  #returnFocus = () => {
    this.refs.trigger.focus();
  };

  /**
   * @param {SubmitEvent} event
   */
  async submit(event) {
    event.preventDefault();
    if (this.#sending) return;

    const { form, email, submit } = this.refs;

    this.#clearMessages();

    if (!email.validity.valid) {
      this.#showInvalidEmail();
      return;
    }

    this.#sending = true;
    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new URLSearchParams(/** @type {any} */ (new FormData(form))),
        headers: { Accept: 'application/json' },
      });
      const answer = response.ok ? await response.json() : null;

      if (answer?.ok === true) {
        this.#showSuccess();
      } else if (answer?.reason === 'invalid-email') {
        this.#showInvalidEmail();
      } else {
        this.#showStatus(this.dataset.textError ?? '');
      }
    } catch {
      this.#showStatus(this.dataset.textError ?? '');
    } finally {
      this.#sending = false;
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
    }
  }

  #clearMessages() {
    const { email, error, status } = this.refs;

    email.removeAttribute('aria-invalid');
    email.removeAttribute('aria-describedby');
    error.hidden = true;
    error.textContent = '';
    status.textContent = '';
  }

  #showInvalidEmail() {
    const { email, error } = this.refs;

    error.textContent = this.dataset.textInvalidEmail ?? '';
    error.hidden = false;
    email.setAttribute('aria-invalid', 'true');
    email.setAttribute('aria-describedby', error.id);
    email.focus();
  }

  #showSuccess() {
    const { form, status } = this.refs;

    form.hidden = true;
    this.#showStatus(this.dataset.textSuccess ?? '');
    status.focus();
  }

  /**
   * @param {string} text
   */
  #showStatus(text) {
    this.refs.status.textContent = text;
  }
}

if (!customElements.get('restock-signup-component')) {
  customElements.define('restock-signup-component', RestockSignupComponent);
}
